/**
 * Route-pending page templates.
 *
 * These render the SHAPE of a destination page while that route's component
 * chunk is still downloading (mobile: 300-800ms on first visit). Without them
 * the router falls back to the full-page RoutePending mascot, which replaces
 * the whole app at `defaultPendingMs` and reads as "nothing happened, now
 * we're waiting" — the exact feel the perf audit flagged. With them, a detail
 * click swaps to the destination's template immediately and data streams in.
 *
 * Eager-safety: these are referenced from route DEFINITION files (always in
 * the entry chunk), so they may only depend on entry-safe modules (PageHeader
 * family) and must stay dependency-light — no boneyard, hooks, or lazy-chunk
 * imports.
 *
 * Each destination family gets its own template (the ClassDetailTemplatePending
 * precedent): the pending must mirror the real page's header family, identity
 * block, content shape and sticky CTA, or the swap to the loaded page reads as
 * landing somewhere else.
 */
import { PageHeader } from '@/components/v2/page-header';
import { BackButton } from '@/components/v2/page-header/page-header-buttons';
import { PageTitle } from '@/components/v2/page-title';
import styles from './page-template-pending.module.less';

const Block = ({ style }: { style?: React.CSSProperties }) => (
  <div className={styles.block} style={style} aria-hidden="true" />
);

/**
 * Generic media-hero detail template — the _public layout-level fallback for
 * the reviews subtree (its leaf routes all declare their own templates, so
 * this only covers layout-crossing navigations). Pages with a concrete shape
 * (class/instructor/studio/market product/purchases) have their own templates
 * below and must NOT use this one.
 */
export function EntityDetailTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <div className={styles.content}>
        <Block style={{ height: 210, borderRadius: 16 }} />
        <Block style={{ height: 30, width: '65%', marginTop: 18 }} />
        <Block style={{ height: 18, width: '40%', marginTop: 10 }} />
        <Block style={{ height: 120, marginTop: 22 }} />
        <Block style={{ height: 120, marginTop: 14 }} />
      </div>
    </div>
  );
}

/**
 * Class detail template — the real page has no back header: it opens on a
 * full-bleed cover, then title + rating, studio row, instructor card,
 * getting-there section, and the sticky Book Now bar. The pending mirrors
 * that chrome-free structure so the swap to the real page is geometry-stable.
 */
export function ClassDetailTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      {/* Full-bleed cover, flush to the viewport top and sides. */}
      <Block style={{ height: '40dvh', borderRadius: 0 }} />
      <div className={styles.detailContent}>
        <div className={styles.row}>
          <Block style={{ height: 24, width: '55%' }} />
          <Block style={{ height: 24, width: 70, marginLeft: 'auto', flexShrink: 0 }} />
        </div>
        {/* Studio row card: small swatch + name line. */}
        <div className={styles.cardStub}>
          <Block style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
          <Block style={{ height: 14, width: '45%' }} />
        </div>
        {/* Instructor card: avatar circle + name/rating lines. */}
        <div className={styles.cardStub}>
          <Block style={{ width: 48, height: 48, borderRadius: 999, flexShrink: 0 }} />
          <div className={styles.cardLines}>
            <Block style={{ height: 16, width: '55%' }} />
            <Block style={{ height: 12, width: '35%' }} />
          </div>
        </div>
        {/* Getting there: section label + address card. */}
        <div className={styles.sectionStub}>
          <Block style={{ height: 14, width: 110 }} />
          <Block style={{ height: 64, borderRadius: 14 }} />
        </div>
      </div>
      <div className={styles.ctaBar}>
        <Block style={{ height: 50, borderRadius: 14 }} />
      </div>
    </div>
  );
}

/**
 * Instructor detail template — centered identity hero (avatar, name, rating,
 * genre chips) over the review CTA card, the "find them at" studio list
 * cards, and the sticky CTA bar. No back header, like the real page.
 */
export function InstructorDetailTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.centeredHero}>
        <Block style={{ width: 96, height: 96, borderRadius: 999 }} />
        <Block style={{ height: 22, width: '45%' }} />
        <Block style={{ height: 14, width: 120 }} />
        <div className={styles.chipRow}>
          {[0, 1, 2].map((chip) => (
            <Block key={chip} style={{ height: 26, width: 74, borderRadius: 999 }} />
          ))}
        </div>
      </div>
      <div className={styles.detailContent}>
        {/* Review CTA card. */}
        <Block style={{ height: 64, borderRadius: 16 }} />
        {[0, 1].map((row) => (
          <div key={row} className={styles.cardStub}>
            <Block style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0 }} />
            <Block style={{ height: 14, width: '45%' }} />
          </div>
        ))}
      </div>
      <div className={styles.ctaBar}>
        <Block style={{ height: 50, borderRadius: 14 }} />
      </div>
    </div>
  );
}

/**
 * Studio detail template — centered identity hero (logo, name, rating) over
 * the two action buttons, two class cards, the location card, and the sticky
 * "See class schedule" bar. No back header, like the real page.
 */
export function StudioDetailTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.centeredHero}>
        <Block style={{ width: 120, height: 120, borderRadius: 24 }} />
        <Block style={{ height: 22, width: '50%' }} />
        <Block style={{ height: 14, width: 120 }} />
      </div>
      <div className={styles.detailContent}>
        <div className={styles.buttonRow}>
          {[0, 1].map((button) => (
            <Block key={button} style={{ height: 48, flex: 1, width: 'auto', borderRadius: 14 }} />
          ))}
        </div>
        {[0, 1].map((row) => (
          <div key={row} className={styles.cardStub}>
            <Block style={{ width: 64, height: 64, borderRadius: 12, flexShrink: 0 }} />
            <div className={styles.cardLines}>
              <Block style={{ height: 16, width: '70%' }} />
              <Block style={{ height: 12, width: '45%' }} />
            </div>
          </div>
        ))}
        {/* Location card: map-pin stub + address line. */}
        <div className={styles.cardStub}>
          <Block style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0 }} />
          <Block style={{ height: 14, width: '60%' }} />
        </div>
      </div>
      <div className={styles.ctaBar}>
        <Block style={{ height: 50, borderRadius: 14 }} />
      </div>
    </div>
  );
}

/**
 * Market product template — the loaded cross-studio-package page keeps the
 * back header (EntityDetailLayout) and opens on a TEXT hero (title, studio
 * swatches, description), then the 2-column "choose your pass" variant grid,
 * studio row cards, and the sticky "Buy pass" BottomAction. Not the generic
 * media-hero shape, and not the header-less class cover.
 */
export function MarketProductTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <div className={styles.detailContent} style={{ paddingTop: 8 }}>
        {/* Text hero: title, swatch + studios line, description lines. */}
        <Block style={{ height: 28, width: '70%' }} />
        <div className={styles.row} style={{ marginTop: 12 }}>
          <Block style={{ width: 16, height: 16, borderRadius: 999, flexShrink: 0 }} />
          <Block style={{ width: 16, height: 16, borderRadius: 999, flexShrink: 0 }} />
          <Block style={{ width: 16, height: 16, borderRadius: 999, flexShrink: 0 }} />
          <Block style={{ height: 13, width: '45%' }} />
        </div>
        <Block style={{ height: 14, width: '90%', marginTop: 12 }} />
        <Block style={{ height: 14, width: '72%', marginTop: 8 }} />
        {/* Choose-your-pass: section label + 2-column variant card grid. */}
        <div className={styles.sectionStub}>
          <Block style={{ height: 13, width: 130 }} />
          <div className={styles.variantGrid}>
            {[0, 1, 2, 3].map((card) => (
              <Block key={card} style={{ height: 92, borderRadius: 12 }} />
            ))}
          </div>
        </div>
        {/* What's included: studio row cards. */}
        {[0, 1].map((row) => (
          <div key={row} className={styles.cardStub}>
            <Block style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0 }} />
            <div className={styles.cardLines}>
              <Block style={{ height: 14, width: '50%' }} />
              <Block style={{ height: 12, width: '30%' }} />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.ctaBar}>
        <Block style={{ height: 50, borderRadius: 14 }} />
      </div>
    </div>
  );
}

/**
 * Purchase status template (HitPay return page) — the loaded page is a
 * back-header over a CENTERED status view (spinner/icon, headline, muted
 * note, single action button); no media hero, no stacked cards.
 */
export function PurchaseStatusTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <div className={styles.centeredStatus}>
        <Block style={{ width: 64, height: 64, borderRadius: 999 }} />
        <Block style={{ height: 22, width: '55%', marginTop: 16 }} />
        <Block style={{ height: 14, width: '72%', marginTop: 10 }} />
        <Block style={{ height: 44, width: 160, marginTop: 20, borderRadius: 999 }} />
      </div>
    </div>
  );
}

/**
 * Purchase detail template — back header, product title lines, then stacked
 * full-width info cards (summary with amount + status badge, order-details
 * label/value rows, fulfillment), ending in the retry/primary button. Not the
 * generic media-hero shape.
 */
export function PurchaseDetailTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <div className={styles.content}>
        <Block style={{ height: 26, width: '60%' }} />
        <Block style={{ height: 16, width: '45%', marginTop: 8 }} />
        {/* Summary card: product name, amount + badge row. */}
        <div className={styles.cardStub} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <Block style={{ height: 16, width: '65%' }} />
          <div className={styles.row} style={{ marginTop: 10 }}>
            <Block style={{ height: 16, width: 72 }} />
            <Block style={{ height: 16, width: 92, marginLeft: 'auto' }} />
          </div>
        </div>
        {/* Order-details + fulfillment cards: section label + label/value rows. */}
        {[0, 1].map((card) => (
          <div
            key={card}
            className={styles.cardStub}
            style={{ flexDirection: 'column', alignItems: 'stretch' }}
          >
            <Block style={{ height: 13, width: '40%' }} />
            {[0, 1, 2].map((row) => (
              <div key={row} className={styles.row} style={{ marginTop: 10 }}>
                <Block style={{ height: 12, width: '30%' }} />
                <Block style={{ height: 12, width: '38%', marginLeft: 'auto' }} />
              </div>
            ))}
          </div>
        ))}
        {/* Retry / primary action. */}
        <Block style={{ height: 48, marginTop: 16, borderRadius: 999 }} />
      </div>
    </div>
  );
}

/**
 * Community post detail template (author card + comment rows). Mirrors the
 * loaded page's social card (author row, text lines, reaction row) and the
 * comment list under it — not the market/FR shapes this used to be shared
 * with (they have their own templates now).
 */
export function PostDetailTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <div className={styles.content}>
        <div className={styles.socialCardStub}>
          <div className={styles.row}>
            <Block style={{ width: 40, height: 40, borderRadius: 999, flexShrink: 0 }} />
            <Block style={{ height: 14, width: '38%' }} />
            <Block style={{ height: 12, width: 48, marginLeft: 'auto', flexShrink: 0 }} />
          </div>
          <Block style={{ height: 14, marginTop: 14 }} />
          <Block style={{ height: 14, width: '70%', marginTop: 8 }} />
          <Block style={{ height: 20, width: '40%', marginTop: 14 }} />
        </div>
        {[0, 1, 2].map((comment) => (
          <div
            key={comment}
            className={styles.row}
            style={{ marginTop: 16, alignItems: 'flex-start' }}
          >
            <Block style={{ width: 32, height: 32, borderRadius: 999, flexShrink: 0 }} />
            <div className={styles.cardLines}>
              <Block style={{ height: 12, width: '30%' }} />
              <Block style={{ height: 12, width: '75%' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * List/sub-page template. Used bare by the public reviews lists; favourites
 * and my-reviews pass the destination's real title and tab switcher so the
 * chrome renders as it will when loaded (title stays a skeleton block when
 * the loaded page's title is tab-dependent and unknown at pending time).
 */
export function ListPageTemplatePending({
  title,
  subtitle,
  tabs = false,
}: {
  title?: string;
  subtitle?: string;
  tabs?: boolean;
} = {}) {
  return (
    <div className={styles.page} aria-busy="true" aria-label={title}>
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      {title ? <PageTitle title={title} subtitle={subtitle} /> : null}
      <div className={styles.content}>
        {!title && <Block style={{ height: 30, width: '55%' }} />}
        {tabs && (
          <div className={styles.tabBarStub} style={{ marginTop: title ? 8 : 16 }}>
            <div className={styles.tabSegments}>
              <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
              <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
            </div>
          </div>
        )}
        <Block style={{ height: 84, marginTop: 18 }} />
        <Block style={{ height: 84, marginTop: 12 }} />
        <Block style={{ height: 84, marginTop: 12 }} />
        <Block style={{ height: 84, marginTop: 12 }} />
      </div>
    </div>
  );
}

/**
 * Purchases list template — back header, the real "My Purchases" title (the
 * loaded page hardcodes it, so nothing swaps text on arrival), filter/sort
 * dropdown stubs, quick-filter chip row, and wide purchase cards (name,
 * amount + status badge, date lines) instead of generic 84px rows.
 */
export function PurchasesListTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <PageTitle title="My Purchases" subtitle="Your packages & payment history" />
      <div className={styles.content}>
        {/* Toolbar: status + sort dropdown stubs. */}
        <div className={styles.row} style={{ marginTop: 16 }}>
          <Block style={{ height: 40, flex: 1, width: 'auto', borderRadius: 10 }} />
          <Block style={{ height: 40, flex: 1, width: 'auto', borderRadius: 10 }} />
        </div>
        {/* Quick-filter chip row. */}
        <div className={styles.chipRow} style={{ justifyContent: 'flex-start', marginTop: 12 }}>
          {[0, 1, 2, 3].map((chip) => (
            <Block key={chip} style={{ height: 30, width: 76, borderRadius: 999 }} />
          ))}
        </div>
        {/* Purchase cards: name, amount + badge, date. */}
        {[0, 1, 2].map((card) => (
          <div
            key={card}
            className={styles.cardStub}
            style={{ flexDirection: 'column', alignItems: 'stretch', marginTop: 12 }}
          >
            <Block style={{ height: 16, width: '70%' }} />
            <div className={styles.row} style={{ marginTop: 10 }}>
              <Block style={{ height: 16, width: 72 }} />
              <Block style={{ height: 16, width: 92, marginLeft: 'auto' }} />
            </div>
            <Block style={{ height: 12, width: '45%', marginTop: 10 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Settings template — back header over the side-by-side announcement +
 * onboarding alpha cards, the App|Security tab switcher, then preference
 * sections (heading + control block). Not a plain back-header row list.
 */
export function SettingsTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader leftAction={<BackButton onClick={() => undefined} />} />
      <div className={styles.content}>
        {/* Announcement + onboarding refresher cards side by side. */}
        <div className={styles.buttonRow}>
          <Block style={{ height: 88, flex: 1, width: 'auto', borderRadius: 10 }} />
          <Block style={{ height: 88, flex: 1, width: 'auto', borderRadius: 10 }} />
        </div>
        {/* Tab switcher (App | Security). */}
        <div className={styles.tabBarStub} style={{ marginTop: 24 }}>
          <div className={styles.tabSegments}>
            <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
            <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
          </div>
        </div>
        {/* Preference sections: heading + control block. */}
        {[0, 1].map((section) => (
          <div key={section} className={styles.sectionStub} style={{ marginTop: 24 }}>
            <Block style={{ height: 16, width: '35%' }} />
            <Block style={{ height: 56, borderRadius: 12 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Profile hub template — the real page has NO back action (the header's left
 * slot is empty and the right slot holds the edit/settings pill pair), leads
 * with the 86px avatar + name/bio row, then the stacked full-width nav cards.
 * A back-header list pending here read as the wrong destination entirely.
 */
export function ProfileHubTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true" aria-label="Profile">
      <PageHeader
        leftAction={<div />}
        rightAction={
          <>
            <Block style={{ width: 40, height: 40, borderRadius: 999 }} />
            <Block style={{ width: 40, height: 40, borderRadius: 999 }} />
          </>
        }
      />
      <div className={styles.content}>
        {/* Identity row: fixed 86px avatar + name/bio lines (real min-height 88). */}
        <div className={styles.row} style={{ alignItems: 'flex-start', minHeight: 88 }}>
          <Block style={{ width: 86, height: 86, borderRadius: 999, flexShrink: 0 }} />
          <div className={styles.cardLines} style={{ paddingTop: 6 }}>
            <Block style={{ height: 41, width: '55%', borderRadius: 12 }} />
            <Block style={{ height: 18, width: '40%', borderRadius: 12 }} />
          </div>
        </div>
        {/* Nav cards: stacked full-width 114px cards. */}
        <div style={{ marginTop: 24 }}>
          {[0, 1, 2, 3].map((card) => (
            <Block
              key={card}
              style={{ height: 114, marginTop: card === 0 ? 0 : 12, borderRadius: 10 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Profile social feed template — back header with the create-post plus stub,
 * the My Posts | Liked tab switcher, then tall (~156px) social feed cards.
 * Generic 84px rows read as a settings-style list, not a feed.
 */
export function SocialFeedTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeader
        leftAction={<BackButton onClick={() => undefined} />}
        rightAction={<Block style={{ width: 40, height: 40, borderRadius: 999 }} />}
      />
      <div className={styles.content}>
        <div className={styles.tabBarStub}>
          <div className={styles.tabSegments}>
            <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
            <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
          </div>
        </div>
        {[0, 1, 2].map((card) => (
          <div key={card} className={styles.socialCardStub} style={{ minHeight: 156 }}>
            <div className={styles.row}>
              <Block style={{ width: 40, height: 40, borderRadius: 999, flexShrink: 0 }} />
              <Block style={{ height: 14, width: '38%' }} />
              <Block style={{ height: 12, width: 48, marginLeft: 'auto', flexShrink: 0 }} />
            </div>
            <Block style={{ height: 14, marginTop: 14 }} />
            <Block style={{ height: 14, width: '70%', marginTop: 8 }} />
            <Block style={{ height: 20, width: '40%', marginTop: 14 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Public dancer profile (/u/$handle) — a header-less, chrome-free page: a
 * centered initial avatar over the display name and @handle. No back header,
 * no list rows; it must read as a standalone identity card.
 */
export function PublicProfileTemplatePending() {
  return (
    <div className={styles.page} aria-busy="true" aria-label="Dancer profile">
      <div className={styles.centeredHero} style={{ paddingTop: 48 }}>
        <Block style={{ width: 96, height: 96, borderRadius: 999 }} />
        <Block style={{ height: 24, width: '50%', marginTop: 8 }} />
        <Block style={{ height: 14, width: 110, marginTop: 8 }} />
      </div>
    </div>
  );
}

/**
 * Form page template. The header family and submit placement follow the real
 * page: review create/edit, create/edit post and the new-FR form close with an
 * X; profile edit and the data-report/listing/FR-edit forms navigate back with
 * an arrow (`variant="arrow"`). Forms whose submit lives in the sticky
 * BottomAction bar pass `cta` so the pending ends in the same fixed bar
 * instead of an inline pill; pages with no in-content submit at all (profile
 * edit saves from the header) render neither.
 */
export function FormPageTemplatePending({
  title,
  subtitle,
  variant = 'close',
  cta = false,
}: {
  title: string;
  subtitle?: string;
  variant?: 'arrow' | 'close';
  cta?: boolean;
}) {
  return (
    <div className={styles.page} aria-busy="true" aria-label={title}>
      <PageHeader leftAction={<BackButton variant={variant} onClick={() => undefined} />} />
      <div className={`${styles.content} ${cta ? styles.contentCta : ''}`.trimEnd()}>
        <Block style={{ height: 30, width: '55%' }} />
        {subtitle ? <Block style={{ height: 18, width: '40%', marginTop: 10 }} /> : null}
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className={styles.fieldGroup}>
            <Block style={{ height: 14, width: '40%' }} />
            <Block style={{ height: 48, borderRadius: 12 }} />
          </div>
        ))}
        {!cta && <Block style={{ height: 52, marginTop: 24, borderRadius: 999 }} />}
      </div>
      {cta && (
        <div className={styles.ctaBar}>
          <Block style={{ height: 50, borderRadius: 14 }} />
        </div>
      )}
    </div>
  );
}

/** Community feed destination template (top-level route: no back button). */
export function CommunityRoutePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.communityHeader}>
        <h1 className={styles.communityTitle}>Community</h1>
        <p className={styles.communitySubtitle}>What is dance without community?</p>
      </div>
      <div className={styles.content}>
        <div className={styles.tabBarStub}>
          <div className={styles.tabSegments}>
            <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
            <Block style={{ height: '100%', flex: 1, width: 'auto' }} />
          </div>
          {/* Create-post plus stub, parked at the bar's right end. */}
          <Block style={{ width: 36, height: 36, borderRadius: 999, flexShrink: 0 }} />
        </div>
        {[0, 1, 2].map((card) => (
          <div key={card} className={styles.socialCardStub}>
            <div className={styles.row}>
              <Block style={{ width: 40, height: 40, borderRadius: 999, flexShrink: 0 }} />
              <Block style={{ height: 14, width: '38%' }} />
              <Block style={{ height: 12, width: 48, marginLeft: 'auto', flexShrink: 0 }} />
            </div>
            <Block style={{ height: 14, marginTop: 14 }} />
            <Block style={{ height: 14, width: '70%', marginTop: 8 }} />
            <Block style={{ height: 20, width: '40%', marginTop: 14 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
